// frontend\src\pages\MyFilms.js
import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  BiTime,
  BiPlus,
  BiPlusCircle,
  BiFilm,
  BiTrash,
  BiEdit,
  BiMinus,
  BiReset,
  BiError,
  BiErrorAlt,
  BiMoney,
  BiSearch,
  BiFilter,
  BiChevronDown,
  BiChevronUp,
  BiPrinter,
} from "react-icons/bi";
import api from "../api/api";
import { FILMS_API } from "../constants";
import { toast } from "react-toastify";
import Loader from "../components/Loader";
import ScrollToTopButton from "../components/ScrollToTopButton";
import { useFileExport } from "../hooks/useFileExport";
import ConfirmModal from "../components/ConfirmModal";

const MyFilms = () => {
  const [films, setFilms] = useState([]);
  const [allFilms, setAllFilms] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [showUseConfirm, setShowUseConfirm] = useState(null);
  const [isFiltersExpanded, setIsFiltersExpanded] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState({
    format: "",
    film_type: "",
    color_type: "",
    quantity_min: "",
    quantity_max: "",
    expiry_date_from: "",
    expiry_date_to: "",
  });
  const [appliedFilters, setAppliedFilters] = useState({});
  const navigate = useNavigate();
  const location = useLocation();
  const { isExporting, exportProgress, exportFile } = useFileExport();

  const applyFiltersLocal = useCallback(
    (filmsData) => {
      let filteredFilms = [...filmsData];

      if (appliedFilters.searchTerm) {
        filteredFilms = filteredFilms.filter((film) =>
          film.name
            .toLowerCase()
            .includes(appliedFilters.searchTerm.toLowerCase())
        );
      }

      if (appliedFilters.format) {
        filteredFilms = filteredFilms.filter(
          (film) => film.format === appliedFilters.format
        );
      }

      if (appliedFilters.film_type) {
        filteredFilms = filteredFilms.filter(
          (film) => film.film_type === appliedFilters.film_type
        );
      }

      if (appliedFilters.color_type) {
        filteredFilms = filteredFilms.filter(
          (film) => film.color_type === appliedFilters.color_type
        );
      }

      if (appliedFilters.quantity_min) {
        filteredFilms = filteredFilms.filter(
          (film) => film.quantity >= parseInt(appliedFilters.quantity_min)
        );
      }

      if (appliedFilters.quantity_max) {
        filteredFilms = filteredFilms.filter(
          (film) => film.quantity <= parseInt(appliedFilters.quantity_max)
        );
      }

      if (appliedFilters.expiry_date_from) {
        filteredFilms = filteredFilms.filter(
          (film) =>
            film.expiry_date &&
            new Date(film.expiry_date) >=
              new Date(appliedFilters.expiry_date_from)
        );
      }

      if (appliedFilters.expiry_date_to) {
        filteredFilms = filteredFilms.filter(
          (film) =>
            film.expiry_date &&
            new Date(film.expiry_date) <=
              new Date(appliedFilters.expiry_date_to)
        );
      }

      setFilms(filteredFilms);
    },
    [appliedFilters]
  );

  const loadData = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await api.get(FILMS_API);
      const filmsData = Array.isArray(response.data) ? response.data : [];
      setAllFilms(filmsData);
      applyFiltersLocal(filmsData);
    } catch (error) {
      toast.error("Ошибка загрузки данных!");
      console.error("Ошибка:", error);
    } finally {
      setIsLoading(false);
    }
  }, [applyFiltersLocal]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (location.state && location.state.shouldRefresh) {
      loadData();
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location, navigate, loadData]);

  const getAvailableFormats = () => {
    const formats = new Set();
    allFilms.forEach((film) => {
      if (film.format) {
        formats.add(film.format);
      }
    });
    return Array.from(formats).sort();
  };

  const availableFormats = getAvailableFormats();

  const getAvailableColorTypes = () => {
    const color_types = new Set();
    allFilms.forEach((film) => {
      if (film.color_type) {
        color_types.add(film.color_type);
      }
    });
    return Array.from(color_types).sort();
  };

  const availableColorTypes = getAvailableColorTypes();

  const getAvailableTypes = () => {
    const film_type = new Set();
    allFilms.forEach((film) => {
      if (film.film_type) {
        film_type.add(film.film_type);
      }
    });
    return Array.from(film_type).sort();
  };

  const availableTypes = getAvailableTypes();

  const applyFilters = () => {
    setAppliedFilters({
      searchTerm,
      ...filters,
    });
  };

  const resetFilters = () => {
    setSearchTerm("");
    setFilters({
      format: "",
      film_type: "",
      color_type: "",
      quantity_min: "",
      quantity_max: "",
      expiry_date_from: "",
      expiry_date_to: "",
    });
    setAppliedFilters({});
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setFilters((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      applyFilters();
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`${FILMS_API}${id}/`);
      setFilms(films.filter((film) => film.id !== id));
      setAllFilms(allFilms.filter((film) => film.id !== id));
      setShowDeleteConfirm(null);
      toast.success("Плёнка удалена!");
    } catch (error) {
      toast.error("Ошибка удаления!");
    }
  };

  const handleUseOne = async (id) => {
    try {
      const response = await api.post(`${FILMS_API}${id}/use_one/`);

      if (response.status === 200) {
        if (response.data && response.data.status === "last item removed") {
          setFilms(films.filter((film) => film.id !== id));
          setAllFilms(allFilms.filter((film) => film.id !== id));
          toast.success("Последняя катушка списана!");
        } else {
          const updatedFilms = films.map((film) =>
            film.id === id ? { ...film, quantity: film.quantity - 1 } : film
          );
          const updatedAllFilms = allFilms.map((film) =>
            film.id === id ? { ...film, quantity: film.quantity - 1 } : film
          );
          setFilms(updatedFilms);
          setAllFilms(updatedAllFilms);
          toast.success("Катушка списана!");
        }
      }
      setShowUseConfirm(null);
    } catch (error) {
      toast.error("Ошибка списания!");
      loadData();
    }
  };

  const handleExportPDF = () => {
    if (films.length === 0) {
      toast.info("Нет данных для экспорта");
      return;
    }
    
    exportFile(
      (options) => api.get(`${FILMS_API}export_pdf/`, options),
      "my_films_catalog.pdf",
      "PDF успешно экспортирован!"
    );
  };

  const isExpired = (expiryDate) => {
    if (!expiryDate) return false;
    return new Date(expiryDate) < new Date();
  };

  const formatPrice = (price) => {
    if (!price) return "-";
    return price.toLocaleString("ru-RU", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const FilmDashboard = () => {
    const formatStats = films.reduce((accumulator, film) => {
      if (!accumulator[film.format]) {
        accumulator[film.format] = {
          total: 0,
          color: 0,
          bw: 0,
          totalCost: 0,
          items: [],
        };
      }
      accumulator[film.format].total += film.quantity;
      if (film.color_type === "color") {
        accumulator[film.format].color += film.quantity;
      } else {
        accumulator[film.format].bw += film.quantity;
      }
      if (film.price_per_roll) {
        accumulator[film.format].totalCost +=
          film.quantity * film.price_per_roll;
      }
      accumulator[film.format].items.push(film);
      return accumulator;
    }, {});

    const formatCount = Object.keys(formatStats).length;
    const cardClass =
      formatCount <= 2
        ? "col-md-6 col-6"
        : formatCount === 3
        ? "col-md-4 col-6"
        : "col-md-3 col-6";

    const lowStockFilms = films.filter((film) => film.quantity <= 2);
    const expiringSoon = films.filter(
      (film) =>
        film.expiry_date &&
        new Date(film.expiry_date) <
          new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
    );

    const expiredFilmsGrouped = expiringSoon.reduce((accumulator, film) => {
      const key = `${film.name}_${film.format}`;
      if (!accumulator[key]) {
        accumulator[key] = {
          name: film.name,
          format: film.format,
          quantity: 0,
          expiry_date: film.expiry_date,
        };
      }
      accumulator[key].quantity += film.quantity;
      return accumulator;
    }, {});

    const totalInvestment = films.reduce((sum, film) => {
      return film.price_per_roll
        ? sum + film.quantity * film.price_per_roll
        : sum;
    }, 0);

    const renderFilmStats = (total, color, bw) => {
      return (
        <div className="text-center">
          <div className="mb-1">
            <span className="me-1">Всего:</span>
            <span style={{ fontSize: "1.2rem", fontWeight: "bold" }}>
              {total}
            </span>
            <span className="ms-1">шт.</span>
          </div>
          {color > 0 && (
            <div className="mb-1">
              <span className="me-1">Цвет:</span>
              <span style={{ fontSize: "1.2rem", fontWeight: "bold" }}>
                {color}
              </span>
              <span className="ms-1">шт.</span>
            </div>
          )}
          {bw > 0 && (
            <div>
              <span className="me-1">Ч/Б:</span>
              <span style={{ fontSize: "1.2rem", fontWeight: "bold" }}>
                {bw}
              </span>
              <span className="ms-1">шт.</span>
            </div>
          )}
        </div>
      );
    };

    return (
      <div className="dashboard mb-4">
        <div className="alert alert-info d-flex align-items-center mb-4">
          <BiMoney className="me-2" size="1.5em" />
          <div>
            Найдено <strong>{films.length}</strong> вид
            {films.length === 1
              ? " "
              : films.length >= 2 && films.length <= 4
              ? "а "
              : "ов "}
            пленки на сумму:{" "}
            <strong>
              {totalInvestment.toLocaleString("ru-RU", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}{" "}
            </strong>
            руб
          </div>
        </div>

        <div className="row g-3 mb-4">
          {Object.entries(formatStats).map(([format, data]) => (
            <div className={cardClass} key={format}>
              <div
                className={`card h-100 ${
                  data.total <= 3 ? "border-warning" : ""
                }`}
              >
                <div className="card-body p-2 p-sm-3">
                  <h5 className="card-title text-center mb-2" style={{ color: "var(--text-primary)" }}>
                    <BiFilm className="me-2" />
                    {format === "sheet"
                      ? "Листовая"
                      : format === "135"
                      ? "формат 135 (35мм)"
                      : "формат " + format}
                  </h5>

                  {renderFilmStats(data.total, data.color, data.bw)}

                  {data.totalCost > 0 && (
                    <div className="small text-muted text-center mt-2">
                      На сумму:{" "}
                      {data.totalCost.toLocaleString("ru-RU", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}{" "}
                      руб
                    </div>
                  )}

                  {data.total <= 3 && (
                    <div className="text-center mt-2">
                      <span className="badge bg-warning text-dark">
                        <BiError className="me-1" />
                        {data.total === 1 ? "Последняя!" : "Мало!"}
                      </span>
                    </div>
                  )}

                  <div className="text-center mt-2">
                    <Link
                      to={{
                        pathname: "/films/new",
                        search: `?format=${encodeURIComponent(format)}`,
                      }}
                      className="small"
                      style={{ color: "var(--btn-primary-bg)" }}
                    >
                      <BiPlus className="me-1" />
                      Добавить
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {(lowStockFilms.length > 0 || expiringSoon.length > 0) && (
          <div className="row g-3 align-items-stretch">
            {lowStockFilms.length > 0 && (
              <div className="col-lg-6 d-flex">
                <div className="alert alert-warning py-2 w-100 d-flex flex-column" style={{ minHeight: '120px' }}>
                  <h5 className="d-flex align-items-center mb-2">
                    <BiErrorAlt className="me-2" />
                    Заканчиваются:
                  </h5>
                  <div className="d-flex flex-wrap gap-1 align-items-start">
                    {lowStockFilms.map((film) => (
                      <span key={film.id} className="badge bg-light text-dark">
                        [{film.format}] {film.name} ({film.quantity} шт.)
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {expiringSoon.length > 0 && (
              <div className="col-lg-6 d-flex">
                <div className="alert alert-danger py-2 w-100 d-flex flex-column" style={{ minHeight: '120px' }}>
                  <h5 className="d-flex align-items-center mb-2">
                    <BiTime className="me-2" />
                    Срок годности:
                  </h5>
                  <div className="d-flex flex-wrap gap-1 align-items-start">
                    {Object.values(expiredFilmsGrouped).map((film, index) => (
                      <span key={index} className="badge bg-light text-dark">
                        [{film.format}] {film.name} ({film.quantity} шт.) до{" "}
                        {new Date(film.expiry_date).toLocaleDateString("ru-RU")}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  if (isLoading) {
    return <Loader />;
  }

  const hasActiveFilters =
    searchTerm ||
    filters.format ||
    filters.film_type ||
    filters.color_type ||
    filters.quantity_min ||
    filters.quantity_max ||
    filters.expiry_date_from ||
    filters.expiry_date_to;

  return (
    <div className="container-fluid mt-4">
      {/* === ДЕСКТОП: Полные кнопки === */}
      <div className="d-none d-lg-flex justify-content-between align-items-center mb-4">
        <h1 className="h1 mb-0" style={{ color: "var(--text-primary)" }}>
          Мои фотоплёнки
        </h1>
        <div className="d-flex gap-2">
          <button
            onClick={handleExportPDF}
            className="btn btn-outline-primary"
            disabled={films.length === 0 || isExporting}
          >
            {isExporting ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Генерация ({exportProgress}%)
              </>
            ) : (
              <>
                <BiPrinter className="me-2" />
                Экспорт в PDF
              </>
            )}
          </button>
          <Link to="/films/new" className="btn btn-primary">
            <BiPlusCircle className="me-2" />
            Новая запись
          </Link>
        </div>
      </div>

      {/* === МОБИЛЬНЫЙ: Компактные кнопки под заголовком === */}
      <div className="d-lg-none d-flex flex-column gap-3 mb-4">
        <h1 className="h4 mb-0" style={{ color: "var(--text-primary)" }}>
          Мои фотоплёнки
        </h1>
        <div className="d-flex gap-2">
          <button
            onClick={handleExportPDF}
            className="btn btn-outline-primary btn-sm flex-fill"
            disabled={films.length === 0 || isExporting}
          >
            {isExporting ? (
              <span className="spinner-border spinner-border-sm" />
            ) : (
              "Экспорт"
            )}
          </button>
          <Link to="/films/new" className="btn btn-primary btn-sm flex-fill">
            Новая
          </Link>
        </div>
      </div>

      {isExporting && (
        <div className="progress mb-3">
          <div
            className="progress-bar progress-bar-striped progress-bar-animated"
            role="progressbar"
            style={{ width: `${exportProgress}%` }}
            aria-valuenow={exportProgress}
            aria-valuemin="0"
            aria-valuemax="100"
          >
            {exportProgress}%
          </div>
        </div>
      )}

      <div className="d-flex justify-content-between align-items-center mb-3">
        <button
          className="btn btn-outline-primary d-flex align-items-center"
          onClick={() => setIsFiltersExpanded(!isFiltersExpanded)}
          type="button"
        >
          <BiFilter className="me-2" />
          Фильтры
          {isFiltersExpanded ? (
            <BiChevronUp className="ms-2" />
          ) : (
            <BiChevronDown className="ms-2" />
          )}
        </button>

        {!isFiltersExpanded && hasActiveFilters && (
          <div className="d-flex flex-wrap gap-2">
            {searchTerm && (
              <span className="badge bg-primary">Поиск: {searchTerm}</span>
            )}
            {filters.format && (
              <span className="badge bg-secondary">
                Формат: {filters.format}
              </span>
            )}
            {filters.film_type && (
              <span className="badge bg-info text-dark">
                Тип:{" "}
                {filters.film_type === "negative"
                  ? "Негатив"
                  : filters.film_type === "reversal"
                  ? "Обращаемая"
                  : "Слайд"}
              </span>
            )}
            {filters.color_type && (
              <span className="badge bg-warning text-dark">
                Цвет: {filters.color_type === "color" ? "Цветная" : "Ч/Б"}
              </span>
            )}
            {(filters.quantity_min || filters.quantity_max) && (
              <span className="badge bg-success">
                Кол-во: {filters.quantity_min || "0"}-
                {filters.quantity_max || "∞"}
              </span>
            )}
            {(filters.expiry_date_from || filters.expiry_date_to) && (
              <span className="badge bg-danger">
                Срок: {filters.expiry_date_from || "∞"}-
                {filters.expiry_date_to || "∞"}
              </span>
            )}
          </div>
        )}
      </div>

      <div className={`card mb-4 ${isFiltersExpanded ? "" : "d-none"}`}>
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-4">
              <label className="form-label">Поиск</label>
              <div className="input-group">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Поиск по названию..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  onKeyDown={handleKeyDown}
                />
              </div>
            </div>

            <div className="col-md-2">
              <label className="form-label">Формат</label>
              <select
                className="form-select"
                name="format"
                value={filters.format}
                onChange={handleFilterChange}
              >
                <option value="">Все форматы</option>
                <option disabled>---</option>
                {availableFormats.map((format) => (
                  <option key={format} value={format}>
                    {format === "110"
                      ? "110"
                      : format === "126"
                      ? "126"
                      : format === "135"
                      ? "135 (35 мм)"
                      : format === "120"
                      ? "120"
                      : format === "220"
                      ? "220"
                      : format === "sheet"
                      ? "Листовая"
                      : format}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-2">
              <label className="form-label">Тип плёнки</label>
              <select
                className="form-select"
                name="film_type"
                value={filters.film_type}
                onChange={handleFilterChange}
              >
                <option value="">Все</option>
                <option disabled>---</option>
                {availableTypes.map((film_type) => (
                  <option key={film_type} value={film_type}>
                    {film_type === "negative"
                      ? "Негативная"
                      : film_type === "reversal"
                      ? "Обращаемая"
                      : "Слайд"}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-2">
              <label className="form-label">Цветность</label>
              <select
                className="form-select"
                name="color_type"
                value={filters.color_type}
                onChange={handleFilterChange}
              >
                <option value="">Все</option>
                <option disabled>---</option>
                {availableColorTypes.map((format) => (
                  <option key={format} value={format}>
                    {format === "color" ? "Цветная" : "Ч/Б"}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-4">
              <div className="row">
                <div className="col-md-6">
                  <label className="form-label">Кол-во от</label>
                  <input
                    type="number"
                    className="form-control"
                    name="quantity_min"
                    value={filters.quantity_min}
                    onChange={handleFilterChange}
                    min="0"
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Кол-во до</label>
                  <input
                    type="number"
                    className="form-control"
                    name="quantity_max"
                    value={filters.quantity_max}
                    onChange={handleFilterChange}
                    min="0"
                  />
                </div>
              </div>
            </div>

            <div className="col-md-4">
              <div className="row">
                <div className="col-md-6">
                  <label className="form-label">Срок от</label>
                  <input
                    type="date"
                    className="form-control"
                    name="expiry_date_from"
                    value={filters.expiry_date_from}
                    onChange={handleFilterChange}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Срок до</label>
                  <input
                    type="date"
                    className="form-control"
                    name="expiry_date_to"
                    value={filters.expiry_date_to}
                    onChange={handleFilterChange}
                  />
                </div>
              </div>
            </div>

            <div className="col-md-4 d-flex justify-content-end align-items-end gap-2">
              <div className="d-flex gap-2">
                <button className="btn btn-primary" onClick={applyFilters}>
                  <BiSearch className="me-2" />
                  Применить
                </button>
                <button
                  className="btn btn-outline-secondary"
                  onClick={resetFilters}
                  title="Сбросить фильтры"
                >
                  <BiReset />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {films.length > 0 && <FilmDashboard />}

      {/* === ДЕСКТОП: ТАБЛИЦА === */}
      <div className="d-none d-lg-block">
        <div className="table-responsive">
          <table className="table table-striped table-hover">
            <thead>
              <tr>
                <th>№</th>
                <th>Название</th>
                <th>ISO</th>
                <th>Формат</th>
                <th>Тип</th>
                <th>Цветность</th>
                <th>Кол-во</th>
                <th>Цена (руб/шт)</th>
                <th>Годен до</th>
                <th>Примечания</th>
                <th style={{ width: "140px" }}>Действия</th>
              </tr>
            </thead>
            <tbody>
              {films.length > 0 ? (
                films.map((film, index) => (
                  <tr key={film.id}>
                    <td className="text-center fw-bold">{index + 1}</td>
                    <td>{film.name}</td>
                    <td>{film.iso}</td>
                    <td>
                      {film.format === "sheet"
                        ? "Листовая"
                        : film.format === "135"
                        ? "135 (35мм)"
                        : film.format}
                    </td>
                    <td>
                      {film.film_type === "negative"
                        ? "Негатив"
                        : film.film_type === "reversal"
                        ? "Обращаемая"
                        : "Слайд"}
                    </td>
                    <td>
                      {film.color_type === "color" ? (
                        <span className="badge bg-success">Цветная</span>
                      ) : (
                        <span className="badge bg-dark">Ч/Б</span>
                      )}
                    </td>
                    <td>{film.quantity} шт.</td>
                    <td>{formatPrice(film.price_per_roll)}</td>
                    <td
                      className={isExpired(film.expiry_date) ? "text-danger" : ""}
                    >
                      {film.expiry_date
                        ? new Date(film.expiry_date).toLocaleDateString("ru-RU")
                        : "-"}
                    </td>
                    <td className="small">{film.notes || "-"}</td>
                    <td>
                      <div className="d-flex gap-2">
                        <button
                          className="btn btn-sm btn-outline-success"
                          onClick={() => setShowUseConfirm(film.id)}
                          title="Списать катушку"
                          disabled={film.quantity <= 0}
                        >
                          <BiMinus />
                        </button>

                        <Link
                          to={`/films/edit/${film.id}`}
                          className="btn btn-sm btn-outline-primary"
                          title="Редактировать"
                        >
                          <BiEdit />
                        </Link>

                        <button
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => setShowDeleteConfirm(film.id)}
                          title="Удалить"
                        >
                          <BiTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="11" className="text-center py-4">
                    <div className="alert alert-info mb-0">
                      {hasActiveFilters ? (
                        "Ничего не найдено. Попробуйте изменить параметры поиска."
                      ) : (
                        <>
                          Нет сохранённых плёнок.{" "}
                          <Link to="/films/new" className="alert-link">
                            Добавьте первую
                          </Link>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* === МОБИЛЬНЫЕ: КАРТОЧКИ === */}
      <div className="d-lg-none">
        {films.length > 0 ? (
          films.map((film, index) => (
            <div key={film.id} className="card mb-3">
              <div className="card-body">
                <h6 className="card-title mb-2">
                  <span className="fw-bold me-2">{index + 1}.</span>
                  {film.name}
                </h6>
                <div className="small text-muted mb-2">
                  <strong>ISO:</strong> {film.iso} | <strong>Формат:</strong>{" "}
                  {film.format === "sheet"
                    ? "Листовая"
                    : film.format === "135"
                    ? "135 (35мм)"
                    : film.format}
                </div>
                <div className="small text-muted mb-2">
                  <strong>Тип:</strong>{" "}
                  {film.film_type === "negative"
                    ? "Негатив"
                    : film.film_type === "reversal"
                    ? "Обращаемая"
                    : "Слайд"}{" "}
                  | <strong>Цветность:</strong>{" "}
                  {film.color_type === "color" ? (
                    <span className="badge bg-success">Цветная</span>
                  ) : (
                    <span className="badge bg-dark">Ч/Б</span>
                  )}
                </div>
                <div className="small text-muted mb-2">
                  <strong>Кол-во:</strong> {film.quantity} шт. |{" "}
                  <strong>Цена:</strong> {formatPrice(film.price_per_roll)}
                </div>
                <div className="small text-muted mb-2">
                  <strong>Годен до:</strong>{" "}
                  <span className={isExpired(film.expiry_date) ? "text-danger" : ""}>
                    {film.expiry_date
                      ? new Date(film.expiry_date).toLocaleDateString("ru-RU")
                      : "-"}
                  </span>
                </div>
                {film.notes && (
                  <p className="small text-muted fst-italic mb-2">{film.notes}</p>
                )}
                <div className="d-flex gap-2">
                  <button
                    className="btn btn-sm btn-outline-success"
                    onClick={() => setShowUseConfirm(film.id)}
                    title="Списать катушку"
                    disabled={film.quantity <= 0}
                  >
                    <BiMinus />
                  </button>

                  <Link
                    to={`/films/edit/${film.id}`}
                    className="btn btn-sm btn-outline-primary"
                    title="Редактировать"
                  >
                    <BiEdit />
                  </Link>

                  <button
                    className="btn btn-sm btn-outline-danger"
                    onClick={() => setShowDeleteConfirm(film.id)}
                    title="Удалить"
                  >
                    <BiTrash />
                  </button>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="alert alert-info">
            {hasActiveFilters ? (
              "Ничего не найдено. Попробуйте изменить параметры поиска."
            ) : (
              <>
                Нет сохранённых плёнок.{" "}
                <Link to="/films/new" className="alert-link">
                  Добавьте первую
                </Link>
              </>
            )}
          </div>
        )}
      </div>

      <ScrollToTopButton />

      {/* Модальное окно подтверждения удаления */}
      <ConfirmModal
        isOpen={!!showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(null)}
        onConfirm={() => handleDelete(showDeleteConfirm)}
        title="Подтверждение удаления"
        message="Вы уверены, что хотите удалить эту плёнку? Это действие нельзя отменить."
        confirmText="Удалить"
        cancelText="Отмена"
        type="delete"
      />

      {/* Модальное окно подтверждения списания */}
      <ConfirmModal
        isOpen={!!showUseConfirm}
        onClose={() => setShowUseConfirm(null)}
        onConfirm={() => handleUseOne(showUseConfirm)}
        title="Подтверждение списания"
        message="Вы уверены, что хотите списать одну катушку этой плёнки?"
        confirmText="Списать"
        cancelText="Отмена"
      />
    </div>
  );
};

export default MyFilms;